import { requestBuilder } from '../../http/RequestBuilder';
import type {
    BackdoorRothPreflightResponse,
    ChangeRebalancerModelRequest,
    ImportApexAccountRequest,
    ImportApexAccountResponse,
    PairBackdoorRothRequest,
    RebalancerAccountFilter,
    RebalancerAccountsByMembership,
} from '../types/RebalancerTypes';

export const REBALANCER_ACCOUNTS_QUERY_KEY = '/api/admin/rebalancer/accounts';

/**
 * List rebalancer accounts with optional filter.
 * @example GET /api/admin/rebalancer/accounts?filter=ALL_OPEN
 */
export const useGetRebalancerAccounts = requestBuilder.query<
    RebalancerAccountsByMembership[],
    { filter?: RebalancerAccountFilter }
>({
    endpoint: ({ filter }) =>
        filter != null
            ? `/api/admin/rebalancer/accounts?filter=${filter}`
            : '/api/admin/rebalancer/accounts',
    queryKey: ({ filter }) =>
        filter != null
            ? `/api/admin/rebalancer/accounts?filter=${filter}`
            : '/api/admin/rebalancer/accounts',
});

/**
 * Change the rebalancer model for an Apex account.
 * @example POST /api/admin/rebalancer/change-model
 */
export const useChangeRebalancerModel = requestBuilder.mutation<
    { success: boolean },
    ChangeRebalancerModelRequest
>({
    endpoint: '/api/admin/rebalancer/change-model',
    method: 'POST',
});

/**
 * Backdoor Roth preflight check - validates the pairing and returns contribution info.
 * @example GET /api/admin/rebalancer/backdoor-roth-preflight?tradApexAccountId=...&rothApexAccountId=...
 */
export const useGetBackdoorRothPreflight = requestBuilder.query<
    BackdoorRothPreflightResponse,
    { tradApexAccountId: string; rothApexAccountId: string }
>({
    endpoint: ({ tradApexAccountId, rothApexAccountId }) =>
        `/api/admin/rebalancer/backdoor-roth-preflight?tradApexAccountId=${tradApexAccountId}&rothApexAccountId=${rothApexAccountId}`,
    queryKey: ({ tradApexAccountId, rothApexAccountId }) =>
        `/api/admin/rebalancer/backdoor-roth-preflight/${tradApexAccountId}/${rothApexAccountId}`,
});

/**
 * Pair a Traditional IRA and Roth IRA for backdoor Roth.
 * @example POST /api/admin/rebalancer/pair-backdoor-roth
 */
export const usePairBackdoorRoth = requestBuilder.mutation<
    { success: boolean },
    PairBackdoorRothRequest
>({
    endpoint: '/api/admin/rebalancer/pair-backdoor-roth',
    method: 'POST',
});

/**
 * Import an externally-created Apex UTMA account into the Fruitful database.
 * Supports dry-run mode for previewing changes before execution.
 * @example POST /api/admin/rebalancer/import-account
 */
export const useImportApexAccount = requestBuilder.mutation<
    ImportApexAccountResponse,
    ImportApexAccountRequest
>({
    endpoint: '/api/admin/rebalancer/import-account',
    method: 'POST',
});
